import path from 'node:path';
import { readFile } from 'node:fs/promises';
import { outputFile, pathExists } from '@wix/cli-fs';
import type { JSCodeshift } from 'jscodeshift';
import jscodeshift from 'jscodeshift';
import { outdent } from 'outdent';

const DEFAULT_EXTENSIONS_TS = outdent`
  import { app } from '@wix/astro/builders';

  export default app();
`;

const CUSTOM_EXTENSIONS_PROJECT_EXTENSIONS_TS = outdent`
  import { app } from '@wix/custom-extensions';

  export default app();
`;

interface ModifyExtensionsEntryOption {
  projectFolder: string;
  extensionName: string;
  relativePath: string;
  isCustomExtensionsProject: boolean;
}

export async function modifyExtensionsEntry({
  projectFolder,
  extensionName,
  relativePath,
  isCustomExtensionsProject,
}: ModifyExtensionsEntryOption) {
  const file = path.join(projectFolder, 'src', 'extensions.ts');

  const extensionsExist = await pathExists(file);
  const source = extensionsExist
    ? await readFile(file, 'utf-8')
    : isCustomExtensionsProject
      ? CUSTOM_EXTENSIONS_PROJECT_EXTENSIONS_TS
      : DEFAULT_EXTENSIONS_TS;

  const j = jscodeshift.withParser('tsx');
  const root = j(source);

  const uniqueExtensionName = addImport(root, j, extensionName, relativePath);
  if (uniqueExtensionName != null) {
    addUseCall(root, j, uniqueExtensionName);
  }

  const modifiedCode = root.toSource({ quote: 'single' });
  await outputFile(file, modifiedCode);
}

function addImport(
  root: ReturnType<JSCodeshift>,
  j: JSCodeshift,
  local: string,
  from: string
) {
  const existingImports = root.find(j.ImportDeclaration, {
    source: { value: from },
  });

  if (existingImports.length > 0) {
    return;
  }

  let finalLocal = local;
  let counter = 1;

  const allSpecifiers = root
    .find(j.ImportSpecifier)
    .paths()
    .map((p) => p.node.local?.name)
    .concat(
      root
        .find(j.ImportDefaultSpecifier)
        .paths()
        .map((p) => p.node.local?.name)
    );

  while (allSpecifiers.includes(finalLocal)) {
    finalLocal = `${local}${counter++}`;
  }

  const newImport = j.importDeclaration(
    [j.importDefaultSpecifier(j.identifier(finalLocal))],
    j.literal(from)
  );

  const lastImport = root.find(j.ImportDeclaration).at(-1);
  if (lastImport.length > 0) {
    lastImport.insertAfter(newImport);
  } else {
    root.get().node.program.body.unshift(newImport);
  }

  return finalLocal;
}

function addUseCall(
  root: ReturnType<JSCodeshift>,
  j: JSCodeshift,
  extensionName: string
) {
  const exportDefaults = root.find(j.ExportDefaultDeclaration);

  for (const path of exportDefaults.paths()) {
    const declaration = path.node.declaration;

    if (declaration.type === 'CallExpression') {
      path.node.declaration = j.callExpression(
        j.memberExpression(declaration, j.identifier('use')),
        [j.identifier(extensionName)]
      );
    }

    if (declaration.type === 'Identifier') {
      path.node.declaration = j.callExpression(
        j.memberExpression(j.identifier(declaration.name), j.identifier('use')),
        [j.identifier(extensionName)]
      );
    }
  }
}
